/**
 * The Learn Center catalog — ONE source of product knowledge, two consumers.
 *
 *   1. The Learn Center panel renders it: browsable, searchable, grouped, with
 *      the tutorial films attached to the topics they teach.
 *   2. The Guide is grounded in it: the assistant may only describe what is in
 *      here, filtered to what THIS user can actually reach.
 *
 * NAMING. The tenant file system is already called "The Library" in the
 * product. This is the "Learn Center" so the two are never the same word in
 * the same sentence — the same reason the journey is never called
 * "onboarding" in code (that route is the invite wizard).
 *
 * CONTENT IS AUTHORED HERE, INLINE, as `{ en, he }`. It is deliberately NOT
 * in the i18n translation files: this is versioned product content — the same
 * call the intake decks make — and keeping a topic's English and Hebrew on the
 * same screen is what stops them drifting apart. Chrome (buttons, labels)
 * still goes through `t()`.
 *
 * ACCURACY IS THE WHOLE VALUE. Every topic here describes something that
 * exists today. A topic that promises a control the product does not have is
 * worse than no topic at all, because the Guide will repeat it with total
 * confidence. `scripts/journey/check-anchors.mjs` asserts every route and
 * every anchor still resolves.
 */

import type { JourneyClipId } from '../config/journeyConfig';

/** Roles, in the hierarchy `hasRole` already enforces. */
export type LearnRole = 'user' | 'admin' | 'group_admin' | 'super_admin';

/** Localized content. Same shape the intake decks use. */
export interface L {
  en: string;
  he: string;
}

export interface LearnTopic {
  id: string;
  section: LearnSectionId;
  /** Every flag must be ON for this topic to exist for a tenant. */
  flags?: string[];
  /** Minimum role. Absent = everyone. */
  role?: LearnRole;
  title: L;
  /** One sentence: what this is and why anyone should care. */
  summary: L;
  /** How to actually do it. Short, imperative, in order. */
  steps?: L[];
  /** Tutorial film, when one exists. */
  film?: JourneyClipId;
  /** Tenant-relative route where this lives ('' = home). */
  route?: string;
  /** Free-text matching help for search and for the Guide. */
  keywords: string[];
}

export type LearnSectionId =
  | 'start'
  | 'chat'
  | 'agents'
  | 'knowledge'
  | 'build'
  | 'together'
  | 'run';

export interface LearnSection {
  id: LearnSectionId;
  title: L;
  blurb: L;
}

export const LEARN_SECTIONS: readonly LearnSection[] = [
  {
    id: 'start',
    title: { en: 'Getting started', he: 'מתחילים' },
    blurb: {
      en: 'The home page, your agents, and where your conversations live.',
      he: 'עמוד הבית, הסוכנים שלכם, והיכן השיחות נשמרות.',
    },
  },
  {
    id: 'chat',
    title: { en: 'Working in a conversation', he: 'עבודה בשיחה' },
    blurb: {
      en: 'Everything the composer can do: models, files, voice, formats.',
      he: 'כל מה שתיבת הכתיבה יודעת: מודלים, קבצים, קול, פורמטים.',
    },
  },
  {
    id: 'agents',
    title: { en: 'Your agents', he: 'הסוכנים שלכם' },
    blurb: {
      en: 'Creating an agent, and every setting that shapes how it answers.',
      he: 'יצירת סוכן, וכל הגדרה שמעצבת את אופן המענה שלו.',
    },
  },
  {
    id: 'knowledge',
    title: { en: 'Knowledge and files', he: 'ידע וקבצים' },
    blurb: {
      en: 'The Library, sharing, and how an agent comes to know your documents.',
      he: 'הספרייה, שיתוף, וכיצד סוכן לומד את המסמכים שלכם.',
    },
  },
  {
    id: 'build',
    title: { en: 'Building with AI', he: 'בנייה בעזרת AI' },
    blurb: {
      en: 'Let the Agent of Agents build for you, and give agents new abilities.',
      he: 'תנו לסוכן הסוכנים לבנות עבורכם, והעניקו לסוכנים יכולות חדשות.',
    },
  },
  {
    id: 'together',
    title: { en: 'Working together', he: 'עבודה משותפת' },
    blurb: {
      en: 'Projects, shared agents, and what your colleagues can see.',
      he: 'פרויקטים, סוכנים משותפים, ומה שהעמיתים שלכם רואים.',
    },
  },
  {
    id: 'run',
    title: { en: 'Running the platform', he: 'ניהול הפלטפורמה' },
    blurb: {
      en: 'Settings, people, usage and cost — for whoever administers this.',
      he: 'הגדרות, אנשים, שימוש ועלות — למי שמנהל את המערכת.',
    },
  },
] as const;

export const LEARN_TOPICS: readonly LearnTopic[] = [
  /* ─────────────────────────── start ─────────────────────────── */
  {
    id: 'home',
    section: 'start',
    route: '',
    film: 'journey-orient',
    title: { en: 'The home page', he: 'עמוד הבית' },
    summary: {
      en: 'Every agent your organization has given you, on one screen. Picking one starts a conversation immediately.',
      he: 'כל סוכן שהארגון העמיד לרשותכם, במסך אחד. בחירה באחד פותחת שיחה מיד.',
    },
    steps: [
      { en: 'Open the home page from the top icon in the sidebar.', he: 'פתחו את עמוד הבית מהאייקון העליון בסרגל הצד.' },
      { en: 'Use the All / Favorites / Recent tabs to narrow the list.', he: 'השתמשו בלשוניות הכול / מועדפים / אחרונים כדי לצמצם את הרשימה.' },
      { en: 'Click any agent card. There is no separate "start" button — the card is the door.', he: 'לחצו על כרטיס סוכן. אין כפתור "התחל" נפרד — הכרטיס עצמו הוא הדלת.' },
      { en: 'Click the star on a card to keep it in Favorites.', he: 'לחצו על הכוכב בכרטיס כדי לשמור אותו במועדפים.' },
    ],
    keywords: ['home', 'start', 'agents', 'gallery', 'favourites', 'favorites', 'recent', 'pick'],
  },
  {
    id: 'history',
    section: 'start',
    title: { en: 'Finding a past conversation', he: 'איתור שיחה קודמת' },
    summary: {
      en: 'Everything you have asked is kept in the sidebar, newest first, and it is searchable.',
      he: 'כל מה ששאלתם נשמר בסרגל הצד, מהחדש לישן, וניתן לחיפוש.',
    },
    steps: [
      { en: 'Hover the sidebar to expand it. Recent conversations are listed under the navigation.', he: 'רחפו מעל סרגל הצד כדי להרחיב אותו. השיחות האחרונות מופיעות מתחת לניווט.' },
      { en: 'Use the search icon in that panel to search by title.', he: 'השתמשו באייקון החיפוש בפאנל כדי לחפש לפי כותרת.' },
      { en: 'Switch between a flat list and grouped-by-agent with the view toggle.', he: 'החליפו בין רשימה שטוחה לקיבוץ לפי סוכן באמצעות מתג התצוגה.' },
      { en: 'Conversations are titled automatically from what you asked.', he: 'שם השיחה נוצר אוטומטית ממה ששאלתם.' },
    ],
    keywords: ['history', 'past', 'previous', 'search', 'sidebar', 'conversations', 'find'],
  },

  /* ─────────────────────────── chat ─────────────────────────── */
  {
    id: 'ask',
    section: 'chat',
    title: { en: 'Asking, and stopping an answer', he: 'שאילה, ועצירת תשובה' },
    summary: {
      en: 'Answers stream in as they are written. You never have to wait for one you do not want.',
      he: 'התשובות זורמות בזמן כתיבתן. אין צורך לחכות לתשובה שאינכם רוצים.',
    },
    steps: [
      { en: 'Type in the composer and press Enter. Shift+Enter makes a new line.', he: 'הקלידו בתיבת הכתיבה והקישו Enter. שילוב Shift+Enter פותח שורה חדשה.' },
      { en: 'While an answer streams, the send button becomes a stop button. Press it to end the reply where it is.', he: 'בזמן שהתשובה זורמת, כפתור השליחה הופך לכפתור עצירה. לחצו כדי לסיים את התשובה במקום שבו היא נמצאת.' },
      { en: 'What was already written stays — stopping does not delete it.', he: 'מה שכבר נכתב נשאר — עצירה אינה מוחקת אותו.' },
    ],
    keywords: ['ask', 'send', 'stop', 'streaming', 'cancel', 'enter', 'message'],
  },
  {
    id: 'model-switch',
    section: 'chat',
    film: 'journey-model-switch',
    title: { en: 'Changing the model mid-conversation', he: 'החלפת מודל באמצע שיחה' },
    summary: {
      en: 'One conversation can be answered by several different AI models. The context carries over — nothing is lost in the handoff.',
      he: 'שיחה אחת יכולה להיענות על ידי כמה מודלים שונים. ההקשר עובר איתה — שום דבר לא הולך לאיבוד.',
    },
    steps: [
      { en: 'Click the star icon on the composer toolbar.', he: 'לחצו על אייקון הכוכב בסרגל תיבת הכתיבה.' },
      { en: 'Pick a different model from the list. The one in use is marked.', he: 'בחרו מודל אחר מהרשימה. המודל הפעיל מסומן.' },
      { en: 'Ask your next question — the new model already knows the conversation so far.', he: 'שאלו את השאלה הבאה — המודל החדש כבר מכיר את השיחה עד כה.' },
      { en: 'Which models appear is set per agent by whoever configured it.', he: 'אילו מודלים מוצעים נקבע לכל סוכן על ידי מי שהגדיר אותו.' },
    ],
    keywords: ['model', 'switch', 'change', 'gpt', 'claude', 'gemini', 'swap', 'mid-conversation'],
  },
  {
    id: 'attach',
    section: 'chat',
    film: 'journey-attach',
    title: { en: 'Asking about a file', he: 'שאלה על קובץ' },
    summary: {
      en: 'Attach a document to a message and the agent reads it before answering.',
      he: 'צרפו מסמך להודעה והסוכן יקרא אותו לפני שיענה.',
    },
    steps: [
      { en: 'Click the paperclip on the composer, or drag a file onto the conversation.', he: 'לחצו על אטב הנייר בתיבת הכתיבה, או גררו קובץ אל השיחה.' },
      { en: 'Ask your question in the same message — "summarise this", "what changed here".', he: 'שאלו את שאלתכם באותה הודעה — "סכם את זה", "מה השתנה כאן".' },
      { en: 'A file attached this way belongs to this conversation only. To teach an agent permanently, use the Library or its Context Files.', he: 'קובץ שצורף כך שייך לשיחה הזו בלבד. כדי ללמד סוכן באופן קבוע, השתמשו בספרייה או בקובצי ההקשר שלו.' },
    ],
    keywords: ['file', 'attach', 'upload', 'document', 'pdf', 'excel', 'drag', 'paperclip'],
  },
  {
    id: 'voice',
    section: 'chat',
    flags: ['chat.voice_input'],
    title: { en: 'Talking instead of typing', he: 'דיבור במקום הקלדה' },
    summary: {
      en: 'Dictate your question with the microphone. It transcribes as you speak, in English or Hebrew.',
      he: 'הכתיבו את שאלתכם עם המיקרופון. התמלול נעשה תוך כדי דיבור, באנגלית או בעברית.',
    },
    steps: [
      { en: 'Click the microphone on the composer and allow the browser to use it.', he: 'לחצו על המיקרופון בתיבת הכתיבה ואשרו לדפדפן להשתמש בו.' },
      { en: 'Speak. The text appears in the composer and can be edited before you send.', he: 'דברו. הטקסט מופיע בתיבה וניתן לעריכה לפני השליחה.' },
      { en: 'Dictation adds to what is already written — it never replaces it.', he: 'ההכתבה מתווספת למה שכבר נכתב — היא לעולם לא מחליפה אותו.' },
    ],
    keywords: ['voice', 'microphone', 'dictate', 'speak', 'talk', 'speech', 'audio'],
  },
  {
    id: 'artifacts',
    section: 'chat',
    flags: ['modules.agent_outputs'],
    title: { en: 'Getting a document, deck or sheet back', he: 'קבלת מסמך, מצגת או גיליון' },
    summary: {
      en: 'Some agents can answer with a real file — a document, a presentation, a spreadsheet — not just text.',
      he: 'סוכנים מסוימים יכולים להשיב בקובץ אמיתי — מסמך, מצגת, גיליון — ולא רק בטקסט.',
    },
    steps: [
      { en: 'Ask for the format you want: "make this a presentation".', he: 'בקשו את הפורמט הרצוי: "הפוך את זה למצגת".' },
      { en: 'The file opens in a panel beside the conversation, where you can read it.', he: 'הקובץ נפתח בפאנל לצד השיחה, שם ניתן לקרוא אותו.' },
      { en: 'Download it, or export it to your cloud drive from the same panel.', he: 'הורידו אותו, או ייצאו אותו לענן מאותו פאנל.' },
      { en: 'Ask for changes in the conversation and the file is rebuilt.', he: 'בקשו שינויים בשיחה והקובץ ייבנה מחדש.' },
    ],
    keywords: ['artifact', 'document', 'powerpoint', 'presentation', 'excel', 'word', 'export', 'download', 'file output'],
  },
  {
    id: 'behavior',
    section: 'chat',
    flags: ['agents.behavior_switching'],
    title: { en: 'Switching how an agent behaves', he: 'החלפת אופן ההתנהגות של סוכן' },
    summary: {
      en: 'When an agent offers behaviour presets, you can change its manner mid-conversation without editing it.',
      he: 'כאשר לסוכן מוגדרים מצבי התנהגות, ניתן לשנות את אופיו באמצע שיחה בלי לערוך אותו.',
    },
    steps: [
      { en: 'Open the mode menu on the composer — it appears only on agents that have presets.', he: 'פתחו את תפריט המצבים בתיבת הכתיבה — הוא מופיע רק בסוכנים שהוגדרו להם מצבים.' },
      { en: 'Pick a mode, for example a short answer versus a thorough one.', he: 'בחרו מצב, למשל תשובה קצרה מול תשובה יסודית.' },
      { en: 'The change applies from your next message onward.', he: 'השינוי חל מההודעה הבאה שלכם ואילך.' },
    ],
    keywords: ['behavior', 'behaviour', 'mode', 'preset', 'tone', 'style', 'switch'],
  },
  {
    id: 'feedback',
    section: 'chat',
    flags: ['agents.message_rating'],
    title: { en: 'Telling an agent how it did', he: 'משוב על תשובה' },
    summary: {
      en: 'Rating an answer is how the people who maintain the agent learn what to keep and what to fix.',
      he: 'דירוג תשובה הוא הדרך שבה מתחזקי הסוכן לומדים מה לשמר ומה לתקן.',
    },
    steps: [
      { en: 'Use the thumbs under any answer. The rating is saved the moment you click.', he: 'השתמשו בסימני האגודל שמתחת לכל תשובה. הדירוג נשמר ברגע הלחיצה.' },
      { en: 'Either thumb offers quick reasons and room for a sentence. That sentence is the useful part, for good answers as much as bad ones.', he: 'כל אגודל מציע סיבות מהירות ומקום למשפט. המשפט הזה הוא החלק המועיל, בתשובות טובות כמו בגרועות.' },
      { en: 'Press the thumb again to change or remove your feedback.', he: 'לחצו שוב על האגודל כדי לשנות או להסיר את המשוב.' },
      { en: 'Ratings reach whoever administers the agent, not the public.', he: 'הדירוגים מגיעים למי שמנהל את הסוכן, לא לכלל המשתמשים.' },
    ],
    keywords: ['rating', 'feedback', 'thumbs', 'wrong', 'bad answer', 'good answer', 'praise', 'comment', 'report', 'improve'],
  },

  /* ─────────────────────────── agents ─────────────────────────── */
  {
    id: 'what-is-agent',
    section: 'agents',
    title: { en: 'What an agent actually is', he: 'מהו סוכן, בעצם' },
    summary: {
      en: 'An agent is an AI assistant with a job: a personality you write, a model behind it, and knowledge you give it.',
      he: 'סוכן הוא עוזר AI עם תפקיד: אישיות שאתם כותבים, מודל שמאחוריו, וידע שאתם מעניקים לו.',
    },
    steps: [
      { en: 'The personality (System Instructions) decides how it answers and what it refuses.', he: 'האישיות (הוראות מערכת) קובעת איך הוא עונה וממה הוא נמנע.' },
      { en: 'The model decides how capable it is. It can be changed at any time.', he: 'המודל קובע עד כמה הוא חזק. ניתן להחליף אותו בכל עת.' },
      { en: 'Its knowledge — context files, Library folders, skills — decides what it actually knows about your company.', he: 'הידע שלו — קובצי הקשר, תיקיות מהספרייה, מיומנויות — קובע מה הוא באמת יודע על החברה שלכם.' },
      { en: 'Two agents with the same model can behave completely differently. The difference is what you wrote and what you gave it.', he: 'שני סוכנים עם אותו מודל יכולים להתנהג אחרת לגמרי. ההבדל הוא מה שכתבתם ומה שנתתם להם.' },
    ],
    keywords: ['agent', 'what is', 'bot', 'assistant', 'concept', 'understand'],
  },
  {
    id: 'create-agent',
    section: 'agents',
    role: 'admin',
    route: '/bots',
    film: 'journey-create-agent',
    title: { en: 'Creating an agent', he: 'יצירת סוכן' },
    summary: {
      en: 'Two required fields and one button. Everything else can wait until it exists.',
      he: 'שני שדות חובה וכפתור אחד. כל השאר יכול לחכות לאחר היצירה.',
    },
    steps: [
      { en: 'Go to Agents and press New Agent.', he: 'עברו לעמוד הסוכנים ולחצו על סוכן חדש.' },
      { en: 'Give it a name and write its System Instructions — who it is and how it should answer, in plain words.', he: 'תנו לו שם וכתבו את הוראות המערכת — מי הוא וכיצד עליו לענות, במילים פשוטות.' },
      { en: 'Description, colour and model are optional; sensible defaults are already chosen.', he: 'תיאור, צבע ומודל הם אופציונליים; ברירות מחדל סבירות כבר נבחרו.' },
      { en: 'Press Create Agent. It is yours alone until you share it.', he: 'לחצו על צור סוכן. הוא שלכם בלבד עד שתשתפו אותו.' },
    ],
    keywords: ['create', 'new agent', 'make', 'build', 'add agent', 'system instructions', 'personality'],
  },
  {
    id: 'agent-powers',
    section: 'agents',
    role: 'admin',
    route: '/bots',
    film: 'journey-agent-powers',
    title: { en: 'Every setting on an agent', he: 'כל ההגדרות של סוכן' },
    summary: {
      en: 'Open any agent to find its tabs: who it is, what it reads, what it can do, and what it produces.',
      he: 'פתחו סוכן כדי למצוא את הלשוניות שלו: מי הוא, מה הוא קורא, מה הוא יודע לעשות, ומה הוא מפיק.',
    },
    steps: [
      { en: 'Details — name, personality, colour, model, and the toggles: web search, knowledge search, behaviour presets.', he: 'פרטים — שם, אישיות, צבע, מודל, והמתגים: חיפוש באינטרנט, חיפוש בידע, מצבי התנהגות.' },
      { en: 'Context Files — documents this agent always has at hand.', he: 'קובצי הקשר — מסמכים שהסוכן תמיד מחזיק בהישג יד.' },
      { en: 'Skills — abilities you switch on, like reading spreadsheets or building a deck.', he: 'מיומנויות — יכולות שמדליקים, כמו קריאת גיליונות או בניית מצגת.' },
      { en: 'Artifacts and Connectors — the file formats it may produce, and the outside sources it may reach, such as a wiki it may search.', he: 'תוצרים וחיבורים — פורמטי הקבצים שהוא רשאי להפיק, והמקורות החיצוניים שהוא מגיע אליהם, כמו ויקי שהוא רשאי לחפש בו.' },
    ],
    keywords: ['edit agent', 'settings', 'tabs', 'skills', 'context files', 'configure', 'change agent'],
  },
  {
    id: 'share-agent',
    section: 'agents',
    film: 'journey-share-agent',
    role: 'admin',
    route: '/bots',
    title: { en: 'Sharing an agent with your team', he: 'שיתוף סוכן עם הצוות' },
    summary: {
      en: 'A new agent is private. Sharing it puts it on your colleagues’ home pages.',
      he: 'סוכן חדש הוא פרטי. שיתוף שלו מציב אותו בעמוד הבית של העמיתים שלכם.',
    },
    steps: [
      { en: 'On the Agents page, use the share action on the agent’s row.', he: 'בעמוד הסוכנים, השתמשו בפעולת השיתוף בשורת הסוכן.' },
      { en: 'Choose who may use it.', he: 'בחרו מי רשאי להשתמש בו.' },
      { en: 'They see the agent, not its instructions — the personality stays with its administrators.', he: 'הם רואים את הסוכן, לא את ההוראות שלו — האישיות נשארת אצל מנהליו.' },
    ],
    keywords: ['share agent', 'team', 'colleagues', 'publish', 'private', 'permissions'],
  },

  /* ─────────────────────────── knowledge ─────────────────────────── */
  {
    id: 'library',
    section: 'knowledge',
    flags: ['modules.library'],
    role: 'admin',
    route: '/vault',
    film: 'journey-library',
    title: { en: 'The Library', he: 'הספרייה' },
    summary: {
      en: 'Your organization’s file system: nested folders with automatic summaries, shared to people or to agents.',
      he: 'מערכת הקבצים של הארגון: תיקיות מקוננות עם תקצירים אוטומטיים, המשותפות לאנשים או לסוכנים.',
    },
    steps: [
      { en: 'Open the Library from the sidebar. Folders you own sit under My files; the shelf everyone shares is under Organization.', he: 'פתחו את הספרייה מסרגל הצד. התיקיות שלכם תחת הקבצים שלי; המדף המשותף תחת ארגון.' },
      { en: 'A folder you create is yours alone until you share it — not even an administrator sees inside it.', he: 'תיקייה שתיצרו היא שלכם בלבד עד שתשתפו אותה — אפילו מנהל אינו רואה בתוכה.' },
      { en: 'Upload files with New, or drag them in. Rename anything by clicking its name.', he: 'העלו קבצים עם חדש, או גררו אותם פנימה. שנו שם בלחיצה על השם.' },
      { en: 'Take things back out from the three-dots menu: a file downloads as itself, a folder as a .zip.', he: 'הוציאו דברים החוצה מתפריט שלוש הנקודות: קובץ יורד כמו שהוא, תיקייה כקובץ zip.' },
      { en: 'Library links, at the top, shows exactly who and what reaches each folder.', he: '"קישורי ספרייה", בראש העמוד, מציג בדיוק מי ומה מגיע לכל תיקייה.' },
    ],
    keywords: ['library', 'vault', 'files', 'folders', 'documents', 'storage', 'upload', 'download', 'zip', 'rename', 'library links'],
  },
  {
    id: 'library-share',
    section: 'knowledge',
    flags: ['modules.library'],
    role: 'admin',
    route: '/vault',
    title: { en: 'Sharing a folder', he: 'שיתוף תיקייה' },
    summary: {
      en: 'Share the folder, not the file — everything inside travels with it, including what you add later.',
      he: 'שתפו את התיקייה, לא את הקובץ — כל מה שבתוכה נוסע איתה, כולל מה שתוסיפו בהמשך.',
    },
    steps: [
      { en: 'Use the share action on a folder to open the sharing manager.', he: 'השתמשו בפעולת השיתוף על תיקייה כדי לפתוח את מנהל השיתוף.' },
      { en: 'Permissions — give a colleague or the whole organization the right to view, edit, delete or re-share.', he: 'הרשאות — העניקו לעמית או לכל הארגון הרשאת צפייה, עריכה, מחיקה או שיתוף מחדש.' },
      { en: 'Rights only ever widen going down: a subfolder can never be more restricted than the folder above it.', he: 'הרשאות רק מתרחבות כלפי מטה: תת-תיקייה לעולם לא תהיה מוגבלת יותר מהתיקייה שמעליה.' },
    ],
    keywords: ['share folder', 'permissions', 'access', 'colleague', 'organization', 'rights', 'who can see', 'library links'],
  },
  {
    id: 'library-to-agent',
    section: 'knowledge',
    flags: ['modules.library'],
    role: 'admin',
    route: '/vault',
    title: { en: 'Teaching an agent from the Library', he: 'לימוד סוכן מתוך הספרייה' },
    summary: {
      en: 'Link a folder to an agent and it answers from those documents — permanently, not just for one message.',
      he: 'קשרו תיקייה לסוכן והוא יענה מתוך אותם מסמכים — לצמיתות, לא רק להודעה אחת.',
    },
    steps: [
      { en: 'Open the sharing manager on the folder and switch to Agents.', he: 'פתחו את מנהל השיתוף על התיקייה ועברו ללשונית סוכנים.' },
      { en: 'Choose the agent — or a skill, if several agents should all get it at once.', he: 'בחרו את הסוכן — או מיומנות, אם כמה סוכנים אמורים לקבל אותה יחד.' },
      { en: 'Searchable is the normal choice: the agent looks in the folder when a question needs it.', he: '"ניתן לחיפוש" היא הבחירה הרגילה: הסוכן מחפש בתיקייה כשהשאלה מצריכה זאת.' },
      { en: 'In prompt puts the content in front of the agent on every single turn. Powerful, and more expensive — use it for short, always-relevant material.', he: '"בתוך הפרומפט" מציב את התוכן מול הסוכן בכל תור. חזק, ויקר יותר — השתמשו בזה לחומר קצר ורלוונטי תמיד.' },
    ],
    keywords: ['link folder', 'agent knowledge', 'rag', 'teach agent', 'documents', 'context', 'searchable'],
  },
  {
    id: 'context-files',
    section: 'knowledge',
    role: 'admin',
    route: '/bots',
    title: { en: 'Context files on one agent', he: 'קובצי הקשר על סוכן אחד' },
    summary: {
      en: 'Files attached directly to a single agent, for material only it needs.',
      he: 'קבצים המצורפים ישירות לסוכן יחיד, לחומר שרק הוא זקוק לו.',
    },
    steps: [
      { en: 'Open the agent and go to Context Files.', he: 'פתחו את הסוכן ועברו ללשונית קובצי הקשר.' },
      { en: 'Upload what this agent should always have.', he: 'העלו את מה שהסוכן הזה צריך תמיד.' },
      { en: 'Use the Library instead when several agents need the same material — one folder beats the same file uploaded five times.', he: 'העדיפו את הספרייה כשכמה סוכנים צריכים את אותו חומר — תיקייה אחת עדיפה על אותו קובץ שהועלה חמש פעמים.' },
    ],
    keywords: ['context files', 'agent files', 'upload to agent', 'knowledge', 'attach permanently'],
  },

  /* ─────────────────────────── build ─────────────────────────── */
  {
    id: 'aoa',
    section: 'build',
    flags: ['modules.agent_of_agents'],
    role: 'group_admin',
    route: '/agent-of-agents',
    title: { en: 'The Agent of Agents', he: 'סוכן הסוכנים' },
    summary: {
      en: 'Describe the agent you want in a sentence and it builds it with you — personality, knowledge, skills.',
      he: 'תארו במשפט את הסוכן שאתם רוצים והוא יבנה אותו איתכם — אישיות, ידע, מיומנויות.',
    },
    steps: [
      { en: 'Open Agent of Agents and start a session.', he: 'פתחו את סוכן הסוכנים והתחילו סשן.' },
      { en: 'Say what you need: "an agent that answers HR questions for new employees".', he: 'אמרו מה נחוץ לכם: "סוכן שעונה על שאלות משאבי אנוש לעובדים חדשים".' },
      { en: 'It proposes each change and waits. Nothing is written until you approve it.', he: 'הוא מציע כל שינוי וממתין. שום דבר לא נכתב לפני שתאשרו.' },
      { en: 'It can also improve an agent you already have, or test how one handles a scenario.', he: 'הוא גם יכול לשפר סוכן קיים, או לבדוק כיצד סוכן מתמודד עם תרחיש.' },
    ],
    keywords: ['agent of agents', 'aoa', 'build agent', 'automatic', 'generate', 'meta', 'improve agent'],
  },
  {
    id: 'skills',
    section: 'build',
    role: 'admin',
    route: '/skills',
    title: { en: 'Skills', he: 'מיומנויות' },
    summary: {
      en: 'A skill is an ability several agents can share — reading spreadsheets, building a deck, searching a source.',
      he: 'מיומנות היא יכולת שכמה סוכנים יכולים לחלוק — קריאת גיליונות, בניית מצגת, חיפוש במקור.',
    },
    steps: [
      { en: 'Open Skills from the tabs on the Agents page.', he: 'פתחו מיומנויות מהלשוניות בעמוד הסוכנים.' },
      { en: 'A skill carries its own instructions and can carry its own files.', he: 'למיומנות יש הוראות משלה והיא יכולה לשאת קבצים משלה.' },
      { en: 'Switch a skill on for an agent from that agent’s Skills tab.', he: 'הדליקו מיומנות עבור סוכן מלשונית המיומנויות של אותו סוכן.' },
      { en: 'Attaching a folder to a skill gives it to every agent that loads that skill at once.', he: 'צירוף תיקייה למיומנות מעניק אותה לכל הסוכנים שטוענים אותה בבת אחת.' },
    ],
    keywords: ['skills', 'abilities', 'tools', 'capabilities', 'excel', 'add skill'],
  },

  /* ─────────────────────────── together ─────────────────────────── */
  {
    id: 'projects',
    section: 'together',
    flags: ['modules.projects'],
    route: '/projects',
    title: { en: 'Projects', he: 'פרויקטים' },
    summary: {
      en: 'A shared workspace where a group works one problem: its own brief, its own files, conversations everyone can join.',
      he: 'סביבת עבודה משותפת שבה קבוצה עובדת על בעיה אחת: תדריך משלה, קבצים משלה, ושיחות שכולם יכולים להצטרף אליהן.',
    },
    steps: [
      { en: 'Open Projects and press New project.', he: 'פתחו פרויקטים ולחצו על פרויקט חדש.' },
      { en: 'Write the brief — every conversation started inside the project receives it automatically.', he: 'כתבו את התדריך — כל שיחה שתיפתח בתוך הפרויקט מקבלת אותו אוטומטית.' },
      { en: 'Add files in the Files tab and invite people in Members.', he: 'הוסיפו קבצים בלשונית קבצים והזמינו אנשים בלשונית חברים.' },
      { en: 'Conversations started in the project are visible to its members — unlike your private chats.', he: 'שיחות שנפתחו בפרויקט גלויות לחבריו — בשונה מהשיחות הפרטיות שלכם.' },
    ],
    keywords: ['project', 'shared', 'workspace', 'team', 'collaborate', 'together', 'invite'],
  },
  {
    id: 'concierge',
    section: 'together',
    flags: ['modules.concierge'],
    title: { en: 'The Concierge', he: 'הקונסיירז’' },
    summary: {
      en: 'Not sure which agent you need? Describe the problem and the Concierge points you at the right one.',
      he: 'לא בטוחים לאיזה סוכן אתם זקוקים? תארו את הבעיה והקונסיירז’ יפנה אתכם לנכון.',
    },
    steps: [
      { en: 'Open the Concierge from its card on the home page.', he: 'פתחו את הקונסיירז’ מהכרטיס שלו בעמוד הבית.' },
      { en: 'Describe what you are trying to do, not which agent you want.', he: 'תארו מה אתם מנסים לעשות, לא איזה סוכן אתם רוצים.' },
      { en: 'It offers agent cards you can open straight from the conversation.', he: 'הוא מציע כרטיסי סוכן שניתן לפתוח ישירות מהשיחה.' },
      { en: 'It only ever recommends. It never changes anything.', he: 'הוא רק ממליץ. הוא לעולם אינו משנה דבר.' },
    ],
    keywords: ['concierge', 'which agent', 'recommend', 'help me choose', 'front door'],
  },
  {
    id: 'flows',
    section: 'together',
    flags: ['modules.flows'],
    route: '/flows',
    title: { en: 'Flows', he: 'מסלולים' },
    summary: {
      en: 'Guided courses and lessons your organization builds, with progress tracked per person.',
      he: 'קורסים ושיעורים מובנים שהארגון בונה, עם מעקב התקדמות אישי.',
    },
    steps: [
      { en: 'Open Flows from the sidebar to see the courses published to you.', he: 'פתחו מסלולים מסרגל הצד כדי לראות את הקורסים שפורסמו עבורכם.' },
      { en: 'Work through lessons in order; your progress is remembered.', he: 'התקדמו בשיעורים לפי הסדר; ההתקדמות נשמרת.' },
      { en: 'Teachers can see how their group is doing.', he: 'מורים יכולים לראות את התקדמות הקבוצה שלהם.' },
    ],
    keywords: ['flows', 'courses', 'lessons', 'learning', 'training', 'progress'],
  },

  /* ─────────────────────────── run ─────────────────────────── */
  {
    id: 'settings',
    section: 'run',
    film: 'journey-settings',
    route: '/settings',
    title: { en: 'Your own settings', he: 'ההגדרות שלכם' },
    summary: {
      en: 'Theme, language, and whether the AI is allowed to remember you between conversations.',
      he: 'ערכת נושא, שפה, והאם ה-AI רשאי לזכור אתכם בין שיחות.',
    },
    steps: [
      { en: 'Open Settings from the bottom of the sidebar.', he: 'פתחו הגדרות מתחתית סרגל הצד.' },
      { en: 'Switch between light and dark, and between English and Hebrew — Hebrew turns the whole interface right-to-left.', he: 'החליפו בין בהיר לכהה, ובין אנגלית לעברית — עברית הופכת את כל הממשק לימין-לשמאל.' },
      { en: 'Personalization lets the AI carry what it learns about you across conversations. You can read and clear it.', he: 'התאמה אישית מאפשרת ל-AI לשאת את מה שלמד עליכם בין שיחות. ניתן לקרוא ולמחוק זאת.' },
      { en: 'Change your password here too.', he: 'כאן גם משנים סיסמה.' },
    ],
    keywords: ['settings', 'theme', 'dark mode', 'language', 'hebrew', 'password', 'memory', 'personalization'],
  },
  {
    id: 'users',
    section: 'run',
    role: 'admin',
    route: '/admin/users',
    title: { en: 'Adding people', he: 'הוספת אנשים' },
    summary: {
      en: 'Invite colleagues, set what role they hold, and remove access when someone leaves.',
      he: 'הזמינו עמיתים, קבעו את התפקיד שלהם, והסירו גישה כשמישהו עוזב.',
    },
    steps: [
      { en: 'Open Admin, then Users.', he: 'פתחו ניהול, ואז משתמשים.' },
      { en: 'Invite by email. They set their own password from the invitation.', he: 'הזמינו לפי דוא"ל. הם קובעים סיסמה בעצמם מתוך ההזמנה.' },
      { en: 'The role decides what they can administer, not which agents they see — that is sharing.', he: 'התפקיד קובע מה הם יכולים לנהל, לא אילו סוכנים הם רואים — לכך אחראי השיתוף.' },
    ],
    keywords: ['users', 'invite', 'add people', 'roles', 'admin', 'team members', 'remove access'],
  },
  {
    id: 'admin-scope',
    section: 'run',
    role: 'admin',
    route: '/admin',
    title: { en: 'What your role can reach', he: 'לאן התפקיד שלכם מגיע' },
    summary: {
      en: 'An administrator runs one organization. A group administrator runs theirs and every organization beneath it.',
      he: 'מנהל מפעיל ארגון אחד. מנהל קבוצה מפעיל את שלו וכל ארגון שמתחתיו.',
    },
    steps: [
      { en: 'The Organization tab draws the tree you can reach — everything on that screen is yours to administer, and nothing outside it is.', he: 'לשונית הארגון משרטטת את העץ שאתם מגיעים אליו — כל מה שבמסך הזה בניהולכם, ומה שמחוצה לו לא.' },
      { en: 'Tenants lists the same organizations as rows; Users is the people in them; Agents is every agent in your scope.', he: 'ארגונים מציג את אותם ארגונים כשורות; משתמשים הם האנשים שבהם; סוכנים הם כל סוכן בתחומכם.' },
      { en: 'A role decides what someone can administer. It does not decide which agents they see — that is sharing, and it is separate.', he: 'תפקיד קובע מה מישהו יכול לנהל. הוא אינו קובע אילו סוכנים הוא רואה — לכך אחראי השיתוף, והוא נפרד.' },
      { en: 'Group administrators also get Tenant Context and the brand book, which apply across their whole subtree.', he: 'מנהלי קבוצה מקבלים גם הקשר ארגוני וספר מותג, שחלים על כל תת-העץ שלהם.' },
    ],
    keywords: ['role', 'permissions', 'scope', 'admin', 'group admin', 'hierarchy', 'organization', 'tenants', 'what can i do'],
  },
  {
    id: 'insights',
    section: 'run',
    flags: ['modules.insights'],
    role: 'group_admin',
    route: '/insights',
    title: { en: 'Insights', he: 'תובנות' },
    summary: {
      en: 'Who is using which agent, how much it costs, and what people are actually asking about.',
      he: 'מי משתמש באיזה סוכן, כמה זה עולה, ועל מה אנשים באמת שואלים.',
    },
    steps: [
      { en: 'Open Insights from the sidebar.', he: 'פתחו תובנות מסרגל הצד.' },
      { en: 'Usage shows conversations per agent and per person over a period you choose.', he: 'שימוש מציג שיחות לפי סוכן ולפי אדם בטווח זמן שתבחרו.' },
      { en: 'Cost breaks the same period down by model and by agent.', he: 'עלות מפרקת את אותו טווח לפי מודל ולפי סוכן.' },
      { en: 'Conversation insights summarise themes across many conversations without you reading each one.', he: 'תובנות שיחה מסכמות נושאים לרוחב שיחות רבות בלי לקרוא כל אחת מהן.' },
    ],
    keywords: ['insights', 'analytics', 'usage', 'cost', 'reports', 'statistics', 'dashboard', 'spend'],
  },
  {
    id: 'tenant-context',
    section: 'run',
    role: 'admin',
    flags: ['tenant_context.editor'],
    title: { en: 'Organization context', he: 'הקשר ארגוני' },
    summary: {
      en: 'Background about your company that every agent gets, so you do not repeat it in each personality.',
      he: 'רקע על החברה שכל סוכן מקבל, כדי שלא תחזרו עליו בכל אישיות בנפרד.',
    },
    steps: [
      { en: 'Open it from Admin.', he: 'פתחו זאת מתוך ניהול.' },
      { en: 'Write what is true of the whole organization: what you do, how you speak, what to avoid.', he: 'כתבו את מה שנכון לכל הארגון: במה אתם עוסקים, איך אתם מדברים, ממה להימנע.' },
      { en: 'Keep it short. It is added to every agent, so length costs on every answer.', he: 'שמרו על קיצור. זה מתווסף לכל סוכן, ולכן אורך עולה בכל תשובה.' },
    ],
    keywords: ['organization context', 'company context', 'tenant', 'global instructions', 'background'],
  },
];

/* ─────────────────────────── selection ─────────────────────────── */

const ROLE_RANK: Record<LearnRole, number> = {
  user: 1,
  admin: 2,
  group_admin: 3,
  super_admin: 4,
};

export interface LearnViewer {
  role: LearnRole;
  hasFlag: (flag: string) => boolean;
}

/**
 * The topics THIS person can actually act on.
 *
 * This is the security-relevant seam, and the reason the Guide is grounded in
 * the filtered list rather than the whole catalog: a plain user must never be
 * told to open Insights, and an agent that recommends a door the user cannot
 * open is worse than one that says nothing.
 */
export function visibleTopics(viewer: LearnViewer): LearnTopic[] {
  return LEARN_TOPICS.filter((topic) => {
    if (topic.role && ROLE_RANK[viewer.role] < ROLE_RANK[topic.role]) return false;
    if (topic.flags && !topic.flags.every(viewer.hasFlag)) return false;
    return true;
  });
}

/** Sections that still have at least one topic for this viewer. */
export function visibleSections(topics: LearnTopic[]): LearnSection[] {
  const present = new Set(topics.map((t) => t.section));
  return LEARN_SECTIONS.filter((s) => present.has(s.id));
}

/** Plain substring search over title, summary, steps and keywords. */
export function searchTopics(topics: LearnTopic[], query: string, lang: 'en' | 'he'): LearnTopic[] {
  // EVERY WORD, not the whole phrase. The old version asked whether the
  // haystack contained the query verbatim, so anybody typing the way people
  // actually type — "share a folder", "change the model" — got nothing back
  // while the answer sat two rows down. Words in any order, all of them
  // present; a topic matching more of them sorts first.
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return topics;

  const scored: { topic: LearnTopic; score: number }[] = [];
  for (const t of topics) {
    const title = `${t.title[lang]} ${t.title.en}`.toLowerCase();
    const hay = [
      t.title[lang], t.title.en,
      t.summary[lang], t.summary.en,
      ...(t.steps ?? []).map((s) => s[lang]),
      ...t.keywords,
    ].join(' ').toLowerCase();

    if (!words.every((w) => hay.includes(w))) continue;
    // A word in the title is worth more than the same word buried in a step.
    const score = words.reduce((n, w) => n + (title.includes(w) ? 2 : 1), 0);
    scored.push({ topic: t, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((x) => x.topic);
}
